import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import { flashMessage } from '~/app/actions';
import Button from '~/app/components/Button';
import { S, A, TONES, DARK } from '~/app/ui/tokens';
import { getS3AssetsManagement } from 'shared/lib/clientSettings';
import {
  CONTROL_JSON,
  getAssetFolderPath,
  getLivestreamControlUrl,
  LIVESTREAM_CONTROL_PATH_OPTIONS,
  normalizeLivestreamControlPath,
  withLivestreamControlDefaults,
} from 'shared/widgets/LivestreamVideoPlayer';

/*
 * LivestreamSceneControl — operator control for ff-livestream.
 * Writes control.json to S3; the deployed widget polls that file live.
 */

const UPLOAD_FILES = gql`
  mutation uploadFiles($files: [Upload!]!, $path: String!, $size: Int!) {
    # This file is rewritten in place on purpose: live pages poll it.
    uploadFiles(files: $files, path: $path, size: $size, replace: true) {
      Key
      Name: Key
      Size
      LastModified
      __typename
    }
  }
`;

const SCENE_TYPES = [
  { value: 'live', label: 'Live stream (.m3u8 / YouTube / embed)' },
  { value: 'video', label: 'Video file (mp4)' },
  { value: 'image', label: 'Image (poster)' },
];

function LivestreamSceneControl(props) {
  const { schema, value, onChange, showFlashMessage } = props;
  const control = withLivestreamControlDefaults(value);
  const scenes = Array.isArray(control.scenes) ? control.scenes : [];
  const activeId = control.activeId || '';
  const path = control.path || '';
  const controlUrl = control.controlUrl || '';

  const c = useStyles();
  const [uploadFilesMutation] = useMutation(UPLOAD_FILES);
  const [busy, setBusy] = useState(false);

  // Persist normalized path/controlUrl when loading legacy or hand-edited props.
  useEffect(() => {
    const raw = value && typeof value === 'object' ? value : {};
    const normalized = withLivestreamControlDefaults(raw);
    if ((raw.path || '') !== normalized.path || (raw.controlUrl || '') !== normalized.controlUrl) {
      onChange(schema.key, { ...raw, path: normalized.path, controlUrl: normalized.controlUrl });
    }
  }, [value, onChange, schema.key]);

  const patch = (changes) => onChange(schema.key, withLivestreamControlDefaults({ ...control, ...changes }));

  const assetPath = path ? `${getAssetFolderPath(path)}${CONTROL_JSON}` : '';

  const setPath = (nextPath) => {
    const normalized = normalizeLivestreamControlPath(nextPath);
    patch({
      path: normalized,
      controlUrl: getLivestreamControlUrl(normalized),
    });
  };

  const updateScene = (i, changes) => {
    patch({ scenes: scenes.map((s, idx) => (idx === i ? { ...s, ...changes } : s)) });
  };

  const addScene = () => {
    const id = 'scene-' + Date.now().toString(36);
    patch({ scenes: [...scenes, { id, label: 'New scene', type: 'live', url: '', image: '', streamName: '', tabLabel: '' }] });
  };

  const removeScene = (i) => {
    if (!confirm('Remove this scene?')) return;
    patch({ scenes: scenes.filter((_, idx) => idx !== i) });
  };

  const uploadControl = async (controlObj) => {
    if (!path) {
      alert('Choose a homepage path (US or CN) before going live.');
      return false;
    }
    setBusy(true);
    try {
      const file = new File(
        [JSON.stringify(controlObj)],
        CONTROL_JSON,
        { type: 'application/json;charset=utf-8' }
      );
      await uploadFilesMutation({
        variables: { files: file, path: getAssetFolderPath(path), size: file.size }
      });
      showFlashMessage('Live media switched successfully', 'SUCCESS');
      return true;
    } catch (error) {
      console.error('Failed to update livestream control', error);
      showFlashMessage('Failed to switch live media', 'ERROR');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const goLive = async (sceneId) => {
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    if (!confirm(`Switch the LIVE page to "${scene.label}" now?`)) return;
    const ok = await uploadControl({ activeId: sceneId, scenes, updatedAt: Date.now() });
    if (ok) patch({ activeId: sceneId });
  };

  const pushCurrent = async () => {
    if (!activeId) {
      alert('No scene is live yet — click "Go Live" on a scene first.');
      return;
    }
    const active = scenes.find((s) => s.id === activeId);
    const name = active ? active.label : activeId;
    if (!confirm(`Push your edits to the LIVE page now? Viewers will get the updated scene URLs, and "${name}" stays the active scene.`)) return;
    await uploadControl({ activeId, scenes, updatedAt: Date.now() });
  };

  const refreshFromLive = async () => {
    if (!path) { alert('Choose a homepage path (US or CN) first.'); return; }
    if (!confirm('Replace the scenes shown here with what is currently live? Any unsaved edits in this panel will be overwritten.')) return;
    try {
      const url = `${getS3AssetsManagement()}/${assetPath}?v=${Date.now()}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data && Array.isArray(data.scenes)) {
        patch({ scenes: data.scenes, activeId: data.activeId || '' });
        showFlashMessage('Loaded current live state', 'SUCCESS');
      } else {
        showFlashMessage('Live control file has no scenes', 'ERROR');
      }
    } catch (error) {
      showFlashMessage('No live control file yet (nothing to load)', 'ERROR');
    }
  };

  return (
    <div className={c.root}>
      <div className={c.banner}>
        Clicking <strong>Go Live</strong> switches the live page immediately —
        you do <strong>not</strong> need to Save or Publish the page.
      </div>

      <div className={c.config}>
        <label className={c.field}>
          <span className={c.label}>Homepage</span>
          <select
            className={c.input}
            value={path}
            onChange={(e) => setPath(e.target.value)}
          >
            <option value="">Select homepage…</option>
            {LIVESTREAM_CONTROL_PATH_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className={c.field}>
          <span className={c.label}>Poll interval (seconds)</span>
          <input
            className={c.input}
            type="number"
            min={5}
            value={control.pollSeconds || 15}
            onChange={(e) => patch({ pollSeconds: parseInt(e.target.value, 10) || 15 })}
          />
        </label>
        <div className={c.field}>
          <span className={c.label}>Control URL (read-only)</span>
          <div className={c.readOnly}>{controlUrl || 'Select a homepage above'}</div>
        </div>
        {assetPath && (
          <div className={c.hint}>
            S3 key: <code>{assetPath}</code>
            <br />
            US pages use <strong>ff-homepage</strong>; CN pages use <strong>ff-homepage-cn</strong>.
            Control URL is always on <code>genesis-cdn.ff.com</code> for instant switching.
          </div>
        )}
      </div>

      <div className={c.scenes}>
        {scenes.length === 0 && (
          <div className={c.empty}>No scenes yet — add one to get started.</div>
        )}
        {scenes.map((scene, i) => {
          const isActive = scene.id === activeId;
          return (
            <div key={scene.id || i} className={isActive ? `${c.scene} ${c.sceneActive}` : c.scene}>
              <div className={c.sceneTop}>
                <span className={c.dot} data-on={isActive ? '1' : ''} />
                <input
                  className={c.sceneLabel}
                  value={scene.label || ''}
                  placeholder="Scene name"
                  onChange={(e) => updateScene(i, { label: e.target.value })}
                />
                <select
                  className={c.sceneType}
                  value={scene.type || 'live'}
                  onChange={(e) => updateScene(i, { type: e.target.value })}
                >
                  {SCENE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <Button
                  className={c.goLive}
                  onClick={() => goLive(scene.id)}
                  disabled={busy || !path}
                  primary={isActive}
                  secondary={!isActive}
                >
                  {isActive ? '● Live' : 'Go Live'}
                </Button>
                <button type="button" className={c.remove} onClick={() => removeScene(i)} title="Remove scene">×</button>
              </div>
              {scene.type === 'image' ? (
                <input
                  className={c.input}
                  value={scene.image || ''}
                  placeholder="Image URL"
                  onChange={(e) => updateScene(i, { image: e.target.value })}
                />
              ) : (
                <input
                  className={c.input}
                  value={scene.url || ''}
                  placeholder=".m3u8 / mp4 / YouTube URL"
                  onChange={(e) => updateScene(i, { url: e.target.value })}
                />
              )}
              <div className={c.sceneLabels}>
                <input
                  className={c.input}
                  value={scene.streamName || ''}
                  placeholder="Stream title (optional — uses Labels & links default if blank)"
                  onChange={(e) => updateScene(i, { streamName: e.target.value })}
                />
                <input
                  className={c.input}
                  value={scene.tabLabel || ''}
                  placeholder="Side tab label (optional)"
                  onChange={(e) => updateScene(i, { tabLabel: e.target.value })}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={c.actions}>
        <Button className={c.btn} onClick={addScene} secondary>+ Add scene</Button>
        <Button className={c.btn} onClick={pushCurrent} disabled={busy || !path} secondary>
          Push edits to live
        </Button>
        <Button className={c.btn} onClick={refreshFromLive} disabled={!path} secondary>
          Refresh from live
        </Button>
      </div>
    </div>
  );
}

const useStyles = createUseStyles({
  root: { padding: '4px 8px 16px' },
  banner: {
    background: '#fff7ed',
    border: '1px solid #fdba74',
    color: '#9a3412',
    borderRadius: 6,
    padding: '10px 12px',
    fontSize: 13,
    marginBottom: 16,
    [DARK]: { background: TONES.amber.bg, borderColor: S.amber, color: TONES.amber.fg },
  },
  config: { display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  field: { display: 'flex', flexDirection: 'column', flex: '1 1 200px' },
  label: { fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4, [DARK]: { color: S.fg } },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '7px 9px',
    border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13,
    [DARK]: { borderColor: S.fieldLine, background: S.field, color: S.fg },
  },
  readOnly: {
    width: '100%', boxSizing: 'border-box', padding: '7px 9px',
    border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 12,
    background: '#f9fafb', color: '#4b5563', wordBreak: 'break-all',
    [DARK]: { borderColor: S.line, background: S.chrome, color: S.muted },
  },
  hint: { flex: '1 1 100%', fontSize: 11, color: '#6b7280', lineHeight: 1.5, [DARK]: { color: S.muted } },
  scenes: { display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { fontSize: 13, color: '#9ca3af', padding: '12px 0', [DARK]: { color: S.muted } },
  scene: { border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, background: A.surface, [DARK]: { borderColor: S.line } },
  sceneActive: { borderColor: '#16a34a', background: '#f0fdf4', [DARK]: { borderColor: S.green, background: TONES.green.bg } },
  sceneTop: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  dot: {
    width: 10, height: 10, borderRadius: '50%', background: '#d1d5db', flex: '0 0 auto',
    '&[data-on="1"]': { background: '#16a34a', boxShadow: '0 0 0 3px rgba(22,163,74,0.2)' },
    [DARK]: { background: S.fieldLine, '&[data-on="1"]': { background: S.green, boxShadow: `0 0 0 3px ${TONES.green.bg}` } },
  },
  sceneLabel: { flex: '1 1 auto', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, [DARK]: { borderColor: S.fieldLine, background: S.field, color: S.fg } },
  sceneType: { flex: '0 0 auto', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, [DARK]: { borderColor: S.fieldLine, background: S.field, color: S.fg } },
  sceneLabels: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 },
  goLive: { flex: '0 0 auto' },
  remove: {
    flex: '0 0 auto', border: 'none', background: 'transparent', color: '#9ca3af',
    fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: '0 4px',
    [DARK]: { color: S.muted },
  },
  actions: { display: 'flex', gap: 10, marginTop: 16 },
  btn: {},
});

const mapDispatchToProps = (dispatch) => ({
  showFlashMessage: (...args) => dispatch(flashMessage(...args))
});

export default connect(null, mapDispatchToProps)(LivestreamSceneControl);
